/**
 * The ONLY module in the client that touches `WebSocket`.
 *
 * Everything above this talks to the `Transport` interface. That is what makes
 * "swap the relay for P2P data channels later" a one-file change rather than a
 * rewrite — worth doing even while we are confident we will not switch, and
 * the lessons doc is emphatic about it.
 *
 * It is also the natural home for the network-conditions simulator, because
 * every byte in and out of the game already passes through here.
 */

export interface TransportHandlers {
  onOpen(): void;
  onMessage(data: ArrayBuffer): void;
  onClose(code: number, reason: string): void;
  onError(): void;
}

export interface Transport {
  readonly isOpen: boolean;
  readonly bytesSent: number;
  readonly bytesReceived: number;
  send(data: ArrayBuffer): void;
  /**
   * Hang up. The code and reason travel to the server, so a client that quits
   * for a reason worth knowing (see `CloseCode`) can say so in its own close
   * frame rather than leaving a bare 1000 in the log.
   */
  close(code?: number, reason?: string): void;
}

/**
 * Null-object transport.
 *
 * Exists so that "no session yet" is a normal `Transport` rather than a `null`
 * that every call site has to remember to check. Avoiding that scattered
 * nullable check is worth the twelve lines.
 */
export const NULL_TRANSPORT: Transport = {
  isOpen: false,
  bytesSent: 0,
  bytesReceived: 0,
  send(): void {},
  close(): void {},
};

export interface NetworkConditions {
  /** One-way delay added in each direction. */
  latencyMs: number;
  /** Uniform +/- variation on that delay. */
  jitterMs: number;
  /**
   * Percentage of messages discarded.
   *
   * NOTE: this cannot happen on a real WebSocket, which is reliable and
   * ordered. It is here to exercise the code paths that would matter under an
   * unreliable transport (hold-last-pose on starvation, input redundancy,
   * sequence-number dedup) so that they are not dead code we discover is
   * broken on the day we add WebRTC data channels. Leave it at 0 when
   * measuring anything that is supposed to resemble production.
   */
  lossPercent: number;
}

export const NO_CONDITIONS: NetworkConditions = {
  latencyMs: 0,
  jitterMs: 0,
  lossPercent: 0,
};

export class WebSocketTransport implements Transport {
  bytesSent = 0;
  bytesReceived = 0;

  private socket: WebSocket | null = null;
  private conditions: NetworkConditions;

  /**
   * Delivery deadlines, tracked per direction so that jitter never reorders
   * messages.
   *
   * A naive `setTimeout(fn, latency + random())` reorders packets, which a
   * WebSocket never does — so the simulator would be testing a transport we do
   * not have and hiding bugs in the one we do. Clamping each message's
   * delivery time to at least the previous one's keeps the simulated channel
   * ordered, like the real thing.
   */
  private nextInboundDelivery = 0;
  private nextOutboundDelivery = 0;

  constructor(
    private readonly url: string,
    private readonly handlers: TransportHandlers,
    conditions: NetworkConditions = NO_CONDITIONS,
  ) {
    this.conditions = conditions;
  }

  get isOpen(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  setConditions(conditions: NetworkConditions): void {
    this.conditions = conditions;
  }

  connect(): void {
    const socket = new WebSocket(this.url);
    socket.binaryType = "arraybuffer";
    this.socket = socket;

    socket.onopen = () => this.handlers.onOpen();

    socket.onmessage = (event: MessageEvent) => {
      const data = event.data as ArrayBuffer;
      if (!(data instanceof ArrayBuffer)) return;
      this.bytesReceived += data.byteLength;
      this.deliver("in", () => this.handlers.onMessage(data));
    };

    socket.onclose = (event: CloseEvent) => this.handlers.onClose(event.code, event.reason);
    socket.onerror = () => this.handlers.onError();
  }

  send(data: ArrayBuffer): void {
    const socket = this.socket;
    if (socket === null || socket.readyState !== WebSocket.OPEN) return;
    this.bytesSent += data.byteLength;
    this.deliver("out", () => {
      // Re-check: the socket can close during the simulated delay.
      if (socket.readyState === WebSocket.OPEN) socket.send(data);
    });
  }

  close(code = 1000, reason = "client closed"): void {
    this.socket?.close(code, reason);
    this.socket = null;
  }

  private deliver(direction: "in" | "out", action: () => void): void {
    const { latencyMs, jitterMs, lossPercent } = this.conditions;

    if (latencyMs <= 0 && jitterMs <= 0 && lossPercent <= 0) {
      action();
      return;
    }

    if (lossPercent > 0 && Math.random() * 100 < lossPercent) return;

    const jitter = jitterMs > 0 ? (Math.random() * 2 - 1) * jitterMs : 0;
    const now = performance.now();
    const earliest = now + Math.max(0, latencyMs + jitter);

    const previous = direction === "in" ? this.nextInboundDelivery : this.nextOutboundDelivery;
    const at = Math.max(previous, earliest);
    if (direction === "in") this.nextInboundDelivery = at;
    else this.nextOutboundDelivery = at;

    setTimeout(action, at - now);
  }
}

/**
 * Read simulated conditions from the URL, e.g. `?lag=150&jitter=50`.
 *
 * Deliberately trivial to switch on. A netcode bug that only appears at 150ms
 * is invisible on localhost, and the lessons doc is clear that this class of
 * problem has to be seen rather than unit-tested — so the barrier to seeing it
 * should be a query string, not a code change.
 */
export function conditionsFromUrl(search: string): NetworkConditions {
  const params = new URLSearchParams(search);
  const num = (key: string, fallback: number): number => {
    const raw = params.get(key);
    if (raw === null) return fallback;
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) && value >= 0 ? value : fallback;
  };
  return {
    latencyMs: num("lag", 0),
    jitterMs: num("jitter", 0),
    lossPercent: num("loss", 0),
  };
}
